import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Edit } from "@mui/icons-material";
import unsplash from "../api/unsplash";
import RimborsiConsulenza from "./RimborsiConsulenza";
import ImportContrattiConsulenza from "./ImportContrattiConsulenza";
import SorvegliatiAnnuali from "./SorvegliatiAnnuali";
import { decimalFieldSx } from "./consulenzaFields";

export const areeConsulenza = {
  sicurezza: "Salute e sicurezza sul lavoro",
  sistemi_gestione: "Sistemi di gestione",
  coordinamento_sanitario: "Coordinamento sorveglianza sanitaria",
  igiene_alimentare: "Igiene alimentare",
};
export function decorrenzaDaFirma(firma) {
  return moment(firma, "YYYY-MM-DD", true).isValid()
    ? moment(firma).add(1, "month").startOf("month").format("YYYY-MM-DD")
    : "";
}
const euro = (value) =>
  Number(value || 0).toLocaleString("it-IT", {
    style: "currency",
    currency: "EUR",
  });
const data = (value) => (value ? moment(value).format("DD/MM/YYYY") : "—");
const vuoto = () => {
  const firma = moment().format("YYYY-MM-DD");
  return {
    Area: "sicurezza",
    ModalitaCalcolo: "fisso",
    DataFirma: firma,
    DataInizio: decorrenzaDaFirma(firma),
    DataFine: "",
    CanonePrimoAnno: "",
    CanoneRinnovi: "",
    CadenzaMesi: 3,
    ModalitaEmissione: "anticipata",
    Responsabile: "",
    Note: "",
    Norme: [],
    NoteFatturazione: "",
    AdeguamentoIstat: false,
  };
};
const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("jwt")}` },
});
export default function ContrattiConsulenza({
  clienteId,
  coordinamentoSanitario = false,
  onApriUscite,
}) {
  const endpoint = coordinamentoSanitario
    ? "contrattiConsulenza?settore=medicina"
    : "contrattiConsulenza";
  const areeDisponibili = Object.entries(areeConsulenza).filter(([id]) =>
    coordinamentoSanitario
      ? id === "coordinamento_sanitario"
      : id !== "coordinamento_sanitario",
  );

  const [mese, setMese] = useState(moment().format("YYYY-MM"));
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const version = useRef(0);
  const context = useRef({ clienteId, mese });
  context.current = { clienteId, mese };
  const load = async () => {
    const n = ++version.current;
    const selected = context.current;
    setLoading(true);
    setError("");
    try {
      const response = await unsplash.get(endpoint, {
        ...auth(),
        params: { cliente: selected.clienteId, mese: selected.mese },
      });
      if (n === version.current) setDati(response.data.data);
    } catch (e) {
      if (n === version.current)
        setError(
          e.response?.data?.message || "Impossibile caricare i contratti",
        );
    } finally {
      if (n === version.current) setLoading(false);
    }
  };
  useEffect(() => {
    setDati(null);
    load();
    return () => {
      version.current += 1;
    };
  }, [clienteId, mese]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setForm(null);
    setFormError("");
  }, [clienteId]);
  const change = (field, value) =>
    setForm((previous) => {
      const next = { ...previous, [field]: value };
      if (field === "Area" && value !== "sistemi_gestione") next.Norme = [];
      if (
        field === "ModalitaCalcolo" &&
        value === "sorvegliati" &&
        !previous.ID &&
        !previous.FotografiaIniziale
      )
        next.FotografiaIniziale = {
          Anno: Number(
            (previous.DataInizio || moment().format("YYYY-MM-DD")).slice(0, 4),
          ),
          NumeroSorvegliati: dati?.numeroSorvegliatiAttuali ?? "",
          TariffaSorvegliato: "",
        };
      if (field === "ModalitaCalcolo" && value !== "sorvegliati")
        delete next.FotografiaIniziale;
      if (
        field === "DataFirma" &&
        (!previous.DataInizio ||
          previous.DataInizio === decorrenzaDaFirma(previous.DataFirma))
      )
        next.DataInizio = decorrenzaDaFirma(value);
      if (
        ["DataFirma", "DataInizio"].includes(field) &&
        next.FotografiaIniziale &&
        Number(next.FotografiaIniziale.Anno) ===
          Number(previous.DataInizio?.slice(0, 4)) &&
        next.DataInizio
      )
        next.FotografiaIniziale = {
          ...next.FotografiaIniziale,
          Anno: Number(next.DataInizio.slice(0, 4)),
        };
      return next;
    });
  const save = async () => {
    setSaving(true);
    setFormError("");
    try {
      await unsplash[form.ID ? "put" : "post"](
        endpoint,
        { cliente: clienteId, id: form.ID, contratto: form },
        auth(),
      );
      if (context.current.clienteId === clienteId) setForm(null);
      await load();
    } catch (e) {
      setFormError(
        e.response?.data?.message || "Impossibile salvare il contratto",
      );
    } finally {
      setSaving(false);
    }
  };
  const teorica =
    form &&
    form.ModalitaCalcolo !== "sorvegliati" &&
    form.CanonePrimoAnno !== ""
      ? Number(form.CanonePrimoAnno) / (12 / Number(form.CadenzaMesi))
      : null;
  return (
    <Stack spacing={2}>
      <Box
        sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}
      >
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {coordinamentoSanitario
            ? "Coordinamento sanitario"
            : "Contratti di consulenza"}
        </Typography>
        <Button
          variant="contained"
          className="ButtonMiller"
          startIcon={<Add />}
          onClick={() => {
            setForm({
              ...vuoto(),
              Area: coordinamentoSanitario
                ? "coordinamento_sanitario"
                : "sicurezza",
              Responsabile: dati?.responsabilePredefinito || "",
            });
            setFormError("");
          }}
        >
          Nuovo contratto
        </Button>
      </Box>
      <ImportContrattiConsulenza
        key={`import-${clienteId}-${coordinamentoSanitario}`}
        medicina={coordinamentoSanitario}
        onImportato={load}
      />
      {error && <Alert severity="error">{error}</Alert>}
      {loading && (
        <CircularProgress size={20} aria-label="Caricamento contratti" />
      )}
      {dati && (
        <>
          {Number(dati.cliente.Cessato) === 1 && (
            <Alert severity="warning">
              Cliente cessato: nessuna quota proposta.
            </Alert>
          )}
          {!dati.contratti.length ? (
            <Typography color="text.secondary">
              Nessun contratto di consulenza inserito.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Area",
                      "Stato",
                      "Firma / decorrenza",
                      "Fine",
                      coordinamentoSanitario
                        ? "Calcolo / canone annuo"
                        : "Canone primo anno / rinnovi",
                      "Periodicità",
                      "Emissione",
                      "ISTAT",
                      "Consulente di riferimento",
                      "",
                    ].map((label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dati.contratti.map((c) => (
                    <TableRow key={c.ID}>
                      <TableCell>
                        {areeConsulenza[c.Area]}
                        {c.Norme?.length > 0 && (
                          <Typography variant="caption" display="block">
                            {c.Norme.join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={c.Stato}
                          color={c.Stato === "Attivo" ? "success" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {data(c.DataFirma)} / {data(c.DataInizio)}
                      </TableCell>
                      <TableCell>{data(c.DataFine)}</TableCell>
                      <TableCell>
                        {c.ModalitaCalcolo === "sorvegliati" ? (
                          "Per numero di sorvegliati"
                        ) : (
                          <>
                            {euro(c.CanonePrimoAnno)} / {euro(c.CanoneRinnovi)}
                          </>
                        )}
                      </TableCell>
                      <TableCell>Ogni {c.CadenzaMesi} mesi</TableCell>
                      <TableCell>{c.ModalitaEmissione}</TableCell>
                      <TableCell>
                        {Number(c.AdeguamentoIstat) === 1 ? "Sì" : "No"}
                      </TableCell>
                      <TableCell>
                        {c.ResponsabileNome || c.Responsabile}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          startIcon={<Edit />}
                          onClick={() => {
                            setForm({
                              ...c,
                              DataFine: c.DataFine || "",
                              ModalitaCalcolo: c.ModalitaCalcolo || "fisso",
                              Note: c.Note || "",
                              Norme: c.Norme || [],
                              NoteFatturazione: c.NoteFatturazione || "",
                            });
                            setFormError("");
                          }}
                        >
                          Modifica
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {coordinamentoSanitario &&
            dati.contratti
              .filter((c) => c.ModalitaCalcolo === "sorvegliati")
              .map((c) => (
                <SorvegliatiAnnuali
                  key={c.ID}
                  clienteId={clienteId}
                  contratto={c}
                  anno={Number(mese.slice(0, 4))}
                  onSaved={load}
                  numeroAttuale={dati.numeroSorvegliatiAttuali}
                />
              ))}
          <Box sx={{ p: 2, bgcolor: "#f5f8fa", borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                flexWrap: "wrap",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                Quote previste per la fatturazione
              </Typography>
              <TextField
                type="month"
                size="small"
                label="Mese di emissione"
                value={mese}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => e.target.value && setMese(e.target.value)}
              />
              <Button
                size="small"
                onClick={() =>
                  setMese(moment().add(1, "month").format("YYYY-MM"))
                }
              >
                Prepara mese prossimo
              </Button>
              <Typography sx={{ ml: { sm: "auto" }, fontWeight: 600 }}>
                Totale previsto: {euro(dati.totalePrevisionale ?? dati.totale)}
              </Typography>
            </Box>
            {Number(dati.totaleDaConfermare) > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Il totale previsto comprende {euro(dati.totaleDaConfermare)}{" "}
                basati su fotografie annuali da confermare. Questi importi non
                sono ancora confermati per la fatturazione.
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Il mese selezionato indica quando è prevista l’emissione. Il
              periodo coperto indica a quali mesi si riferisce il canone: con
              emissione anticipata la quota compare all’inizio del periodo, con
              emissione posticipata alla fine. Puoi selezionare un mese futuro
              per preparare la fatturazione.
            </Typography>
            <Alert severity="info" sx={{ mb: 2 }}>
              Prospetto di previsione: non emette fatture e non verifica quelle
              già emesse in AGO. Le quote restano visibili anche dopo la data
              prevista; prima di fatturare verifica quanto già emesso.
            </Alert>
            {!dati.cliente.CodiceAgo && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Codice Ago mancante nell’anagrafica cliente.
              </Alert>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mb: 1 }}
            >
              Per i canoni fissi il conguaglio dei centesimi è nell’ultima quota
              dell’anno contrattuale; per i sorvegliati è a dicembre. Periodi
              parziali e quote senza dati annuali di riferimento richiedono
              verifica e sono esclusi dal totale previsto.
            </Typography>
            {!dati.righe.length ? (
              <Typography>
                Nessuna emissione prevista nel mese selezionato. Un canone
                anticipato può coprire questo mese pur essendo previsto in un
                mese precedente: controlla la decorrenza e la periodicità del
                contratto.
              </Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[
                        "Prestazione",
                        "Periodo coperto",
                        "Emissione prevista",
                        "Anno contratto",
                        "Importo previsto",
                        "Verifiche",
                      ].map((label) => (
                        <TableCell key={label}>{label}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dati.righe.map((r) => (
                      <TableRow key={`${r.Contratto}-${r.DataInizio}`}>
                        <TableCell>
                          {r.Descrizione}
                          {r.ModalitaCalcolo === "sorvegliati" &&
                            r.FotografieUtilizzate?.map((f) => (
                              <Typography
                                key={f.Anno}
                                variant="caption"
                                display="block"
                              >
                                {f.Confermata === false
                                  ? `Previsione ${f.Anno} da ${f.AnnoOrigine}`
                                  : `Fotografia ${f.Anno}`}
                                : {f.NumeroSorvegliati} ×{" "}
                                {euro(f.TariffaSorvegliato)} annui
                              </Typography>
                            ))}
                          {r.NoteFatturazione && (
                            <Typography variant="body2">
                              {r.NoteFatturazione}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {data(r.DataInizio)} – {data(r.DataFine)}
                        </TableCell>
                        <TableCell>{data(r.DataEmissione)}</TableCell>
                        <TableCell>
                          {r.AnnoContrattuale === 1
                            ? "Primo anno"
                            : `Rinnovo ${r.AnnoContrattuale - 1}`}
                        </TableCell>
                        <TableCell>
                          {r.Importo === null ? (
                            <>
                              {r.PrevisioneDaConfermare
                                ? "Previsione da confermare"
                                : "Da verificare"}
                              {r.ImportoTeorico !== null && (
                                <Typography variant="caption" display="block">
                                  {r.PrevisioneDaConfermare
                                    ? "Quota prevista"
                                    : "Quota teorica"}
                                  : {euro(r.ImportoTeorico)}
                                </Typography>
                              )}
                            </>
                          ) : (
                            euro(r.Importo)
                          )}
                        </TableCell>
                        <TableCell>
                          {r.Avvisi.map((a) => (
                            <Typography
                              variant="body2"
                              key={a}
                              color="warning.main"
                            >
                              {a}
                            </Typography>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </>
      )}
      {!coordinamentoSanitario && (
        <RimborsiConsulenza
          key={clienteId}
          clienteId={clienteId}
          mese={mese}
          onApriUscite={onApriUscite}
        />
      )}
      <Dialog
        open={!!form}
        onClose={() => !saving && setForm(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {form?.ID
            ? "Modifica contratto"
            : coordinamentoSanitario
              ? "Nuovo contratto di coordinamento sanitario"
              : "Nuovo contratto di consulenza"}
        </DialogTitle>
        {form && (
          <>
            <DialogContent>
              <Grid container spacing={2} sx={{ pt: 1 }}>
                {formError && (
                  <Grid item xs={12}>
                    <Alert severity="error">{formError}</Alert>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Area consulenza"
                    value={form.Area}
                    SelectProps={{ native: true }}
                    onChange={(e) => change("Area", e.target.value)}
                  >
                    {areeDisponibili.map(([id, label]) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={[
                      ...(dati?.responsabili || []),
                      ...(form.ID &&
                      form.Responsabile &&
                      !dati?.responsabili.some(
                        (u) => Number(u.ID) === Number(form.Responsabile),
                      )
                        ? [
                            {
                              ID: form.Responsabile,
                              username: `${form.ResponsabileNome || form.Responsabile} (assegnazione precedente)`,
                            },
                          ]
                        : []),
                    ]}
                    noOptionsText="Nessun consulente operativo disponibile"
                    value={
                      dati?.responsabili.find(
                        (u) => Number(u.ID) === Number(form.Responsabile),
                      ) ||
                      (form.ID && form.Responsabile
                        ? {
                            ID: form.Responsabile,
                            username: `${form.ResponsabileNome || form.Responsabile} (assegnazione precedente)`,
                          }
                        : null)
                    }
                    isOptionEqualToValue={(option, value) =>
                      Number(option.ID) === Number(value.ID)
                    }
                    getOptionLabel={(u) => u.username}
                    onChange={(e, value) =>
                      change("Responsabile", value?.ID || "")
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        label="Consulente di riferimento"
                        size="small"
                      />
                    )}
                  />
                </Grid>
                {form.Area === "sistemi_gestione" && (
                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={[
                        "ISO 9001",
                        "ISO 45001",
                        "ISO 14001",
                        "UNI/PdR 125:2022",
                      ]}
                      value={form.Norme || []}
                      onChange={(e, value) => change("Norme", value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Norme dei sistemi di gestione"
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                )}
                {coordinamentoSanitario && (
                  <Grid item xs={12}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Modalità di calcolo coordinamento"
                      value={form.ModalitaCalcolo || "fisso"}
                      SelectProps={{ native: true }}
                      onChange={(e) =>
                        change("ModalitaCalcolo", e.target.value)
                      }
                    >
                      <option value="fisso">Canone fisso</option>
                      <option value="sorvegliati">
                        Numero di sorvegliati × tariffa annua
                      </option>
                    </TextField>
                    {form.ModalitaCalcolo === "sorvegliati" && (
                      <>
                        <Alert severity="info" sx={{ mt: 1 }}>
                          {dati?.numeroSorvegliatiAttuali != null
                            ? `Koala rileva attualmente ${dati.numeroSorvegliatiAttuali} lavoratori attivi nella medicina del cliente (esclusi i lavoratori fuori KPI).`
                            : "Conteggio attuale non disponibile."}{" "}
                          È un suggerimento indicativo: verifica e inserisci il
                          numero corretto per il contratto. Per anni passati o
                          futuri il conteggio di oggi non è una fotografia
                          storica.
                        </Alert>
                        {!form.ID && (
                          <>
                            <FormControlLabel
                              label="Imposta subito la prima fotografia annuale"
                              control={
                                <Checkbox
                                  checked={!!form.FotografiaIniziale}
                                  onChange={(e) =>
                                    change(
                                      "FotografiaIniziale",
                                      e.target.checked
                                        ? {
                                            Anno: Number(
                                              (
                                                form.DataInizio ||
                                                moment().format("YYYY-MM-DD")
                                              ).slice(0, 4),
                                            ),
                                            NumeroSorvegliati:
                                              dati?.numeroSorvegliatiAttuali ??
                                              "",
                                            TariffaSorvegliato: "",
                                          }
                                        : null,
                                    )
                                  }
                                />
                              }
                            />
                            {form.FotografiaIniziale && (
                              <Grid container spacing={2} sx={{ mt: 0 }}>
                                {[
                                  [
                                    "Anno",
                                    "Anno prima fotografia",
                                    1900,
                                    2200,
                                    1,
                                  ],
                                  [
                                    "NumeroSorvegliati",
                                    "Numero sorvegliati",
                                    0,
                                    1000000,
                                    1,
                                  ],
                                  [
                                    "TariffaSorvegliato",
                                    "Tariffa annua per sorvegliato (€)",
                                    0,
                                    undefined,
                                    "0.01",
                                  ],
                                ].map(([key, label, min, max, step]) => (
                                  <Grid key={key} item xs={12} sm={4}>
                                    <TextField
                                      fullWidth
                                      required
                                      size="small"
                                      type="number"
                                      label={label}
                                      value={form.FotografiaIniziale[key]}
                                      inputProps={{ min, max, step }}
                                      onChange={(e) =>
                                        change("FotografiaIniziale", {
                                          ...form.FotografiaIniziale,
                                          [key]: e.target.value,
                                        })
                                      }
                                    />
                                  </Grid>
                                ))}
                              </Grid>
                            )}
                          </>
                        )}
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          La prima fotografia viene confermata con il
                          salvataggio del contratto. Puoi anche impostarla dopo
                          in “Sorvegliati annui”. Gli anni successivi propongono
                          i dati dell’ultima fotografia confermata.
                        </Typography>
                      </>
                    )}
                  </Grid>
                )}
                {[
                  ["DataFirma", "Data firma", true],
                  ["DataInizio", "Decorrenza", true],
                  ["DataFine", "Data cessazione / fine effettiva", false],
                ].map(([key, label, required]) => (
                  <Grid item xs={12} sm={4} key={key}>
                    <TextField
                      fullWidth
                      required={required}
                      size="small"
                      type="date"
                      label={label}
                      value={form[key]}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => change(key, e.target.value)}
                    />
                  </Grid>
                ))}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {form.ModalitaCalcolo === "sorvegliati" ? (
                      "Mantieni la decorrenza originaria del contratto. La fine effettiva interrompe le quote. Il numero e la tariffa dei sorvegliati si impostano per anno solare dopo il salvataggio."
                    ) : (
                      <>
                        La decorrenza proposta è il primo del mese successivo
                        alla firma. Per contratti già in mantenimento inserisci
                        la decorrenza originaria. Il primo anno copre i primi 12
                        mesi; dal secondo il calcolo usa automaticamente il
                        canone rinnovi. La fine effettiva è facoltativa:
                        interrompe le quote e non indica la fine della prima
                        annualità.
                      </>
                    )}
                  </Typography>
                </Grid>
                {form.ModalitaCalcolo !== "sorvegliati" &&
                  [
                    ["CanonePrimoAnno", "Canone annuo primo anno (€)"],
                    ["CanoneRinnovi", "Canone annuo rinnovi (€)"],
                  ].map(([key, label]) => (
                    <Grid item xs={12} sm={6} key={key}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        type="number"
                        sx={decimalFieldSx}
                        label={label}
                        value={form[key]}
                        inputProps={{ min: 0, step: "0.01" }}
                        onChange={(e) => change(key, e.target.value)}
                      />
                    </Grid>
                  ))}
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Periodicità"
                    value={form.CadenzaMesi}
                    SelectProps={{ native: true }}
                    onChange={(e) =>
                      change("CadenzaMesi", Number(e.target.value))
                    }
                  >
                    {[
                      [1, "Mensile"],
                      [3, "Trimestrale"],
                      [6, "Semestrale"],
                      [12, "Annuale"],
                    ].map(([id, label]) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Emissione"
                    value={form.ModalitaEmissione}
                    SelectProps={{ native: true }}
                    onChange={(e) =>
                      change("ModalitaEmissione", e.target.value)
                    }
                  >
                    <option value="anticipata">
                      Anticipata (inizio periodo)
                    </option>
                    <option value="posticipata">
                      Posticipata (fine periodo)
                    </option>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Boolean(Number(form.AdeguamentoIstat))}
                        onChange={(event) =>
                          change("AdeguamentoIstat", event.target.checked)
                        }
                      />
                    }
                    label="Adeguamento ISTAT per questo contratto"
                  />
                </Grid>
                {teorica !== null && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Quota indicativa primo anno: {euro(teorica)}. Il primo
                      anno decorre dalla data di inizio contratto.
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Note interne sul contratto"
                    helperText="Promemoria ed eccezioni per uso interno; non vengono riportati nelle quote di fatturazione."
                    value={form.Note}
                    inputProps={{ maxLength: 4000 }}
                    onChange={(e) => change("Note", e.target.value)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Indicazioni per la fatturazione"
                    helperText="Es. riferimento ordine o dicitura concordata. Riportate nelle quote del prospetto; il trasferimento ad AGO richiede il flusso di esportazione."
                    value={form.NoteFatturazione || ""}
                    inputProps={{ maxLength: 4000 }}
                    onChange={(e) => change("NoteFatturazione", e.target.value)}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button disabled={saving} onClick={() => setForm(null)}>
                Annulla
              </Button>
              <Button variant="contained" disabled={saving} onClick={save}>
                {saving ? "Salvataggio…" : "Salva contratto"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}
